// Admin user record (role, conversation access, Directory Sync origin) and its create payload.
import { object, string } from 'yup';
import { BaseModel, CreateBaseModel } from './base.model';
import { isValidEmail, JsonObject, UiOption } from '../helpers';
import { has } from 'lodash';

export type AdminUserRole = 'org:owner' | 'org:admin' | 'org:auditor';

export const AdminRoleLabels: Record<AdminUserRole, string> = {
  'org:owner': 'Owner',
  'org:admin': 'Admin',
  'org:auditor': 'Auditor',
};

export const AdminRoleOptions: UiOption[] = Object.keys(AdminRoleLabels).map((value) => ({
  value,
  label: AdminRoleLabels[value as AdminUserRole],
}));

export const AdminPermissionLabels: Record<string, string> = {
  'conversation-access': 'View User Event Conversation History',
};

export const CONVERSATION_ACCESS_PERMISSION = 'conversation-access';

const DIRECTORY_SYNC_ORIGIN = 'directory-sync';

export class AdminUserModel extends BaseModel {
  orgId: string;
  name: string;
  email: string;
  phone: string;
  role: AdminUserRole;
  conversationAccess: boolean;
  origin: string | null;
  permissions: string[] | null;

  schema = object({
    name: string().required('Name is required'),
    email: string()
      .test('email', 'Please enter a valid email address', isValidEmail)
      .required('Please enter an email address'),
    role: string()
      .oneOf(['org:owner', 'org:admin', 'org:auditor'])
      .required('Please choose a role'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof AdminUserModel, string>;

    this.orgId = data.orgId || '';
    this.name = data.name || '';
    this.email = data.email || '';
    this.phone = data.phone || '';
    this.role = data.role ? (data.role as AdminUserRole) : 'org:auditor';
    this.conversationAccess = has(data, 'conversationAccess') ? !!data.conversationAccess : false;
    this.origin = data.origin || null;
    this.permissions = (props.permissions as string[] | undefined) ?? null;
  }

  get isDirectorySync(): boolean {
    return this.origin === DIRECTORY_SYNC_ORIGIN;
  }

  // records written before permissions existed only carry the conversationAccess boolean
  get effectivePermissions(): string[] {
    if (this.permissions) {
      return this.permissions;
    }

    return this.conversationAccess ? [CONVERSATION_ACCESS_PERMISSION] : [];
  }
}

export class CreateAdminUserModel extends CreateBaseModel {
  name: string;
  email: string;
  role: AdminUserRole;
  conversationAccess: boolean;

  constructor(adminUser: AdminUserModel) {
    super();

    this.name = adminUser.name;
    this.email = String(adminUser.email).trim().toLowerCase();
    this.role = adminUser.role;
    this.conversationAccess = adminUser.conversationAccess;
  }
}
